import React, { useEffect, useState } from "react";
import {
    Clock,
    TestTube,
    FileCheck,
    IndianRupee,
} from "lucide-react";
import { getDashboardData, getDashboarGraph, getDashboarSample } from "./service/dashboard-service";
import { useLoader } from "../../_helper/LoaderContext";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { formatDoctorDisplayDate } from "../../utils/dateTime/dateFormat";
import { useLab } from "../../_helper/PharmacyProvider";
import { useCurrency } from "../../_helper/currencyContext";
import { useCollection } from "../../_helper/dailyCollection";
import { Card, Table } from "reactstrap";
import SuggestionTable from "../SearchPatient/SuggestionTable";
import { patiantSuggestionService } from "../SearchPatient/services/search-patient";

export default function TopSuggestion() {
    const { t } = useTranslation();
    const [data, setData] = useState({});
    const { setLoading } = useLoader();
    const [rawCategories, setRawCategories] = useState([]); // raw dates like "2025-12-19"
    const [rawSampleDates, setRawSampleDates] = useState([]);
    const [categories, setCategories] = useState([]); // formatted dates
    const [sampleAmt, setSampleAmt] = useState([]);
    const [sampleDate, setSampleDate] = useState([]);
    const { currentLab } = useLab();
    const { getCurrencySymbol } = useCurrency();
    const [apiData, setApiData] = useState([]);
    const { daily, setDaily } = useCollection()
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);

    useEffect(() => {
        if (rawCategories.length > 0) {
            setCategories(rawCategories.map(date => formatDoctorDisplayDate(date, currentLab) || date));
        }
    }, [currentLab, rawCategories]);

    useEffect(() => {
        if (rawSampleDates.length > 0) {
            setSampleDate(rawSampleDates.map(date => formatDoctorDisplayDate(date, currentLab) || date));
        }
    }, [currentLab, rawSampleDates]);

    useEffect(() => {
        getData();
        if (currentLab.hospital_id) {
            getSuggestion()
        }
    }, []);

    const getSuggestion = async () => {
        setSuggestionsLoading(true)
        try {
            const suggestionForm = {
                pageNo: 1,
                pageSize: 10,
                clinic_id: currentLab.hospital_id
            };
            const suggestion = await patiantSuggestionService(suggestionForm);
            setSuggestions(suggestion.data?.data?.suggestions || []);
        } catch (error) {
            console.error("Error fetching patient data:", error);
        } finally {
            setSuggestionsLoading(false)
        }
    }

    const getData = async () => {
        setLoading(true);
        try {
            const [res, response, sampleRes] = await Promise.all([
                getDashboardData(),
                getDashboarGraph(),
                getDashboarSample()
            ]);

            if (response.data.success) {
                const graphData = response.data.data || [];
                setApiData(graphData);

                // Extract unique raw dates and sort chronologically
                const uniqueRawDates = [...new Set(graphData.map(item => item.date))];
                uniqueRawDates.sort((a, b) => new Date(a) - new Date(b));

                setRawCategories(uniqueRawDates);
                setCategories(uniqueRawDates.map(date => formatDoctorDisplayDate(date, currentLab) || date));

            }

            if (sampleRes.data.success) {
                const sampleData = sampleRes.data.data || [];
                setSampleAmt(sampleData.map(item => item.count));
                const sampleDates = sampleData.map(item => item.date);
                setRawSampleDates(sampleDates);
                setSampleDate(sampleDates.map(date => formatDoctorDisplayDate(date, currentLab) || date));
            }

            if (res.data?.success) {
                setData(res.data?.data || {});
                localStorage.setItem('collectionAmount', JSON.stringify(res.data.data.collectionAmount));
                setDaily(res.data.data.collectionAmount)
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Group data by currency using raw date as key
    const groupedByCurrency = apiData.reduce((acc, item) => {
        const currency = item.currencyType || currentLab?.baseCurrency || "INR";
        if (!acc[currency]) acc[currency] = {};
        acc[currency][item.date] = item.amount;
        return acc;
    }, {});

    // Build series for each currency, aligned with rawCategories order
    const series = Object.keys(groupedByCurrency).map(currency => ({
        name: `${t("Collection")} (${currency})`,
        data: rawCategories.map(rawDate => groupedByCurrency[currency][rawDate] ?? 0)
    }));

    // Today's total collection across all currencies (optional enhancement)
    const todayRawDate = new Date().toISOString().split('T')[0]; // "2025-12-19"
    const todayTotal = apiData
        .filter(item => item.date === todayRawDate)
        .reduce((sum, item) => sum + (item.amount || 0), 0);

    const chartOptions = {
        chart: {
            id: "collection-chart",
            toolbar: { show: false },
            zoom: { enabled: false },
            parentHeightOffset: 0
        },
        stroke: {
            curve: "smooth",
            width: 3
        },
        xaxis: {
            categories: categories,
            title: {
                text: t("Date"),
                offsetY: -5,
                style: { fontSize: "11px", fontWeight: 500 }
            },
            labels: {
                rotate: -45,
                style: { fontSize: "11px" }
            }
        },
        yaxis: {
            title: {
                text: t("Amount"),
                style: { fontSize: "11px", fontWeight: 500 }
            },
            labels: {
                style: { fontSize: "11px" },
                formatter: (value) => value?.toLocaleString()
            }
        },
        tooltip: {
            y: {
                formatter: function (value, { seriesIndex }) {
                    if (value === 0) return "0";
                    const currencyMatch = series[seriesIndex]?.name.match(/\((.*?)\)/);
                    const currency = currencyMatch ? currencyMatch[1] : currentLab?.baseCurrency;
                    return `${getCurrencySymbol(currency)}${value?.toLocaleString()}`;
                }
            }
        },
        legend: {
            position: "top",
            horizontalAlign: "left",
            offsetY: -5,
            itemMargin: { horizontal: 8, vertical: 0 }
        },
        colors: ["#0d6efd", "#28a745", "#ffc107", "#dc3545", "#6f42c1"],
        dataLabels: { enabled: false },
        grid: {
            borderColor: "#e0e0e0",
            strokeDashArray: 4,
            padding: {
                top: 0,
                right: 10,
                bottom: 0,
                left: 10
            }
        }
    };

    const chartOptionsSample = {
        chart: {
            id: "sample-collection-chart",
            toolbar: { show: false },
            zoom: { enabled: false },
            parentHeightOffset: 0
        },
        colors: ["#1b255b"],
        stroke: {
            curve: "smooth",
            width: 3
        },
        xaxis: {
            categories: sampleDate,
            title: {
                text: t("Date"),
                offsetY: -5,
                style: { fontSize: "11px", fontWeight: 500 }
            },
            labels: {
                rotate: -45,
                style: { fontSize: "11px" }
            }
        },
        yaxis: {
            title: {
                text: t("Sample Count"),
                style: { fontSize: "11px", fontWeight: 500 }
            },
            labels: {
                style: { fontSize: "11px" },
                formatter: (value) => Math.round(value)
            }
        },
        tooltip: {
            y: {
                formatter: (value) => `${value} ${t("samples")}`
            }
        },
        dataLabels: { enabled: false },
        grid: {
            borderColor: "#e0e0e0",
            strokeDashArray: 4,
            padding: {
                top: 0,
                right: 10,
                bottom: 0,
                left: 10
            }
        }
    };

    const seriesSample = [{ name: t("Sample Collection"), data: sampleAmt }];

    const totalReports = (data?.pendingReports || 0) + (data?.completedReports || 0);
    const completionRate = totalReports > 0 ? ((data?.completedReports || 0) / totalReports) * 100 : 0;

    return (
        <div className="row g-3 [&_.card]:tw-mb-0">
            {/* Metrics */}
            <div className="col-12 col-sm-6 col-xl-3">
                <div className="card shadow-sm rounded-4 mb-0" style={{ border: "1px solid #1b255b" }}>
                    <div className="card-body py-2 px-3">
                        <h6 className="fw-semibold text-muted mb-2 tw-text-sm">{t("Today's Collection")}</h6>
                        <h4 className="fw-bold text-success mb-0 tw-text-xl">
                            {getCurrencySymbol(currentLab?.baseCurrency)}{data?.todayCollection?.toFixed(2) || 0}
                        </h4>
                        {/* <p className="small text-muted mb-0">
                            {data?.todayCollection > 0 ? "Total collected today" : "No collections yet"}
                        </p> */}
                    </div>
                </div>
            </div>

            {/* Pending Amount */}
            <div className="col-12 col-sm-6 col-xl-3">
                <div className="card shadow-sm rounded-4 mb-0" style={{ border: "1px solid #1b255b" }}>
                    <div className="card-body py-2 px-3">
                        <h6 className="fw-semibold text-muted d-flex align-items-center gap-2 mb-2 tw-text-sm">
                            <Clock size={16} /> {t("Pending Amounts")}
                        </h6>
                        <h4 className="fw-bold text-danger mb-0 tw-text-xl">
                            {getCurrencySymbol(currentLab?.baseCurrency)}{(data?.pendingAmount || 0)?.toLocaleString()}
                        </h4>
                        {/* <span className="badge bg-warning text-dark">Requires Attention</span> */}
                    </div>
                </div>
            </div>

            {/* Pending Reports */}
            <div className="col-12 col-sm-6 col-xl-3">
                <div className="card shadow-sm rounded-4 mb-0" style={{ border: "1px solid #1b255b" }}>
                    <div className="card-body py-2 px-3">
                        <h6 className="fw-semibold text-muted d-flex align-items-center gap-2 mb-2 tw-text-sm">
                            <TestTube size={16} /> {t("Pending Reports")}
                        </h6>
                        <h4 className="fw-bold text-warning mb-0 tw-text-xl">{data?.pendingReports || 0}</h4>
                        {/* <p className="small text-muted mb-0">Reports in progress</p> */}
                    </div>
                </div>
            </div>

            {/* Completed Reports */}
            <div className="col-12 col-sm-6 col-xl-3">
                <div className="card shadow-sm rounded-4 mb-0" style={{ border: "1px solid #1b255b" }}>
                    <div className="card-body py-2 px-3">
                        <h6 className="fw-semibold text-muted d-flex align-items-center gap-2 mb-2 tw-text-sm">
                            <FileCheck size={16} /> {t("Completed Reports")}
                        </h6>
                        <h4 className="fw-bold text-success mb-0 tw-text-xl">{data?.completedReports || 0}</h4>
                        {/* <p className="small text-muted mb-0">Delivered successfully</p> */}
                    </div>
                </div>
            </div>

            {/* Charts */}
            <div className="col-md-6">
                <div className="card shadow-sm rounded-4 p-3 pb-2 mb-0">
                    <h5 className="fw-semibold mb-2 tw-text-sm">{t("Daily Collection")}</h5>
                    {series.length > 0 ? (
                        <Chart
                            options={chartOptions}
                            series={series}
                            type="line"
                            height={280}
                        />
                    ) : (
                        <div className="text-center text-muted py-4">{t("No collection data available")}</div>
                    )}
                </div>
            </div>

            <div className="col-md-6">
                <div className="card shadow-sm rounded-4 p-3 pb-2 mb-0">
                    <h5 className="fw-semibold mb-2 tw-text-sm">{t("Daily Sample Collection")}</h5>
                    {sampleAmt.length > 0 ? (
                        <Chart
                            options={chartOptionsSample}
                            series={seriesSample}
                            type="line"
                            height={280}
                        />
                    ) : (
                        <div className="text-center text-muted py-4">{t("No sample data available")}</div>
                    )}
                </div>
            </div>

            {/* Top Tests & Packages */}
            <div className="col-lg-6 d-flex flex-column">
                <div className="card shadow-sm rounded-4 flex-grow-1 w-100 mb-0">
                    <div className="card-body p-3 d-flex flex-column">
                        <h5 className="fw-semibold d-flex align-items-center gap-2 mb-3 tw-text-sm">
                            <i className="fa fa-flask" aria-hidden="true"></i> {t("Most Ordered Tests")}
                        </h5>
                        <div
                            className="custom-slim-scrollbar flex-grow-1"
                            style={{
                                maxHeight: "260px",
                                overflowY: "auto",
                                overflowX: "hidden",
                                paddingRight: "4px",
                            }}
                        >
                            {data?.topTests?.length > 0 ? (
                                data.topTests.map((test, index) => (
                                    <div
                                        key={index}
                                        className={`d-flex justify-content-between align-items-center border rounded py-2 px-3 ${index === data.topTests.length - 1 ? "mb-0" : "mb-2"
                                            }`}
                                    >
                                        <div className="d-flex align-items-center gap-2 me-2 overflow-hidden">
                                            <div
                                                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: "26px", height: "26px", fontSize: "12px" }}
                                            >
                                                {index + 1}
                                            </div>
                                            <span className="fw-semibold tw-text-xs text-truncate" title={test?.name}>
                                                {test?.name}
                                            </span>
                                        </div>
                                        <span className="badge bg-primary tw-text-xs flex-shrink-0">
                                            {test?.count} {test?.count === 1 ? t("time") : t("times")}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted mb-0">{t("No test data available")}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-lg-6 d-flex flex-column">
                <div className="card shadow-sm rounded-4 flex-grow-1 w-100 mb-0">
                    <div className="card-body p-3 d-flex flex-column">
                        <h5 className="fw-semibold d-flex align-items-center gap-2 mb-3 tw-text-sm">
                            <i className="bi bi-boxes"></i> {t("Most Popular Packages")}
                        </h5>
                        <div
                            className="custom-slim-scrollbar flex-grow-1"
                            style={{
                                maxHeight: "260px",
                                overflowY: "auto",
                                overflowX: "hidden",
                                paddingRight: "4px",
                            }}
                        >
                            {data?.topPackages?.length > 0 ? (
                                data.topPackages.map((pkg, index) => (
                                    <div
                                        key={index}
                                        className={`d-flex justify-content-between align-items-center border rounded py-2 px-3 ${index === data.topPackages.length - 1 ? "mb-0" : "mb-2"
                                            }`}
                                    >
                                        <div className="d-flex align-items-center gap-2 me-2 overflow-hidden">
                                            <div
                                                className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: "26px", height: "26px", fontSize: "12px" }}
                                            >
                                                {index + 1}
                                            </div>
                                            <span className="fw-semibold tw-text-xs text-truncate" title={pkg?.packageName}>
                                                {pkg?.packageName}
                                            </span>
                                        </div>
                                        <span className="badge bg-primary tw-text-xs flex-shrink-0">
                                            {pkg?.count} {pkg?.count === 1 ? t("time") : t("times")}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted mb-0">{t("No package data available")}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Top  10 suggestion */}
            {currentLab.isHospitalConnected == true && (
                <div className="col-12">
                    <div className="card shadow-sm rounded-4 p-3 mb-4">
                        <h5 className="fw-semibold mb-3 tw-text-base">{t("Top 10 Suggestions")}</h5>
                        <SuggestionTable
                            suggestions={suggestions}
                            loading={suggestionsLoading}
                            showText={false}
                            showAddTest={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}